import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useCart } from "../../contexts/CartContext";
import Navbar from "../../components/feature/Navbar";
import CartDrawer from "../../components/feature/CartDrawer";
import CheckoutForm from "./components/CheckoutForm";
import OrderSummary from "./components/OrderSummary";

const BANK_INFO = {
  bank: "國泰世華銀行",
  branch: "中和分行",
  code: "013",
  account: "045-03-501536-5",
  holder: "曜泰貿易有限公司",
};

interface OrderData {
  orderNumber: string;
  name: string;
  phone: string;
  city: string;
  district: string;
  address: string;
  totalPrice: number;
}

function generateOrderNumber(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const rand = Math.floor(10000 + Math.random() * 90000);
  return `EL${y}${m}${d}${rand}`;
}

interface SuccessModalProps {
  order: OrderData;
  onClose: () => void;
}

function SuccessModal({ order, onClose }: SuccessModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(BANK_INFO.account);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const fullAddress = `${order.city}${order.district}${order.address}`;

  const orderRows = [
    { label: "訂單編號", value: order.orderNumber, highlight: true },
    { label: "收件人", value: `${order.name}　${order.phone}` },
    { label: "配送地址", value: fullAddress },
    { label: "付款方式", value: "銀行轉帳" },
    { label: "應付金額", value: `NT$${order.totalPrice.toLocaleString()}`, highlight: true },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal content */}
      <div className="relative bg-[#FAFAFA] w-full max-w-md max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-zinc-500 hover:text-zinc-700 transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        <div className="p-6">
          {/* Success icon + title */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 flex items-center justify-center bg-[#1B4332] text-white rounded-full mx-auto mb-4">
              <i className="ri-check-line text-3xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-[#18181B] mb-2">訂單成立！</h1>
            <p className="text-sm text-zinc-500 leading-relaxed">
              請在 <strong className="text-[#1B4332]">3 天</strong>內完成匯款，我們確認收款後即安排出貨
            </p>
          </div>

          {/* Order details card */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden mb-4">
            {orderRows.map(({ label, value, highlight }) => (
              <div
                key={label}
                className="flex items-start justify-between px-5 py-3 border-b border-zinc-100 last:border-0"
              >
                <span className="text-sm text-zinc-400 shrink-0 mr-4">{label}</span>
                <span
                  className={`text-sm text-right font-medium break-all ${
                    highlight ? "text-[#E53E3E]" : "text-zinc-700"
                  }`}
                >
                  {value}
                </span>
              </div>
            ))}
          </div>

          {/* Bank info card */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden mb-4">
            {/* Header */}
            <div className="flex items-center gap-2 px-5 py-3 bg-[#EBF4EF] border-b border-[#C6DDD1]">
              <i className="ri-bank-line text-[#1B4332] text-base"></i>
              <span className="text-sm font-semibold text-[#1B4332]">請匯款至以下帳戶</span>
            </div>

            {/* Bank rows */}
            <div className="divide-y divide-zinc-100">
              <div className="flex items-center justify-between px-5 py-3">
                <span className="text-sm text-zinc-400">銀行</span>
                <span className="text-sm text-zinc-700 font-medium">{BANK_INFO.bank}　{BANK_INFO.branch}</span>
              </div>
              <div className="flex items-center justify-between px-5 py-3">
                <span className="text-sm text-zinc-400">金融代號</span>
                <span className="text-sm text-zinc-700 font-medium">{BANK_INFO.code}</span>
              </div>
              <div className="flex items-center justify-between px-5 py-3">
                <span className="text-sm text-zinc-400">帳號</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#1B4332] font-semibold tracking-wider">{BANK_INFO.account}</span>
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1 px-2.5 py-1 text-[11px] border rounded-full transition-colors cursor-pointer whitespace-nowrap border-[#1B4332] text-[#1B4332] hover:bg-[#1B4332] hover:text-white"
                  >
                    <i className={copied ? "ri-check-line" : "ri-file-copy-line"}></i>
                    {copied ? "已複製" : "複製"}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between px-5 py-3">
                <span className="text-sm text-zinc-400">戶名</span>
                <span className="text-sm text-zinc-700 font-medium">{BANK_INFO.holder}</span>
              </div>
              <div className="flex items-center justify-between px-5 py-3">
                <span className="text-sm text-zinc-400">匯款金額</span>
                <span className="text-sm text-[#E53E3E] font-bold">NT${order.totalPrice.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Reminder notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 mb-5 flex items-start gap-3">
            <i className="ri-error-warning-line text-amber-500 text-base mt-0.5 shrink-0"></i>
            <p className="text-xs text-amber-700 leading-relaxed">
              請在 <strong>3 天</strong>內完成匯款並保留收據。匯款後請聯繫客服告知訂單編號{" "}
              <strong className="text-amber-800">{order.orderNumber}</strong>，我們確認後安排出貨。
            </p>
          </div>

          {/* Contact info */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden mb-5">
            <div className="px-5 py-3 border-b border-zinc-100">
              <span className="text-xs font-semibold text-zinc-400 tracking-widest uppercase">聯絡方式</span>
            </div>
            <div className="divide-y divide-zinc-100">
              <div className="flex items-start gap-4 px-5 py-3.5">
                <div className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-full shrink-0">
                  <i className="ri-time-line text-zinc-500 text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#18181B] mb-0.5">客服時間</p>
                  <p className="text-xs text-zinc-500 leading-relaxed">
                    週一至週五：09:00 – 18:00<br />
                    週六：10:00 – 16:00<br />
                    週日及國定假日：休息
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 px-5 py-3.5">
                <div className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-full shrink-0">
                  <i className="ri-mail-line text-zinc-500 text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#18181B] mb-0.5">電子郵件</p>
                  <p className="text-xs text-zinc-500 leading-relaxed">
                    service@yotaistore.com.tw<br />
                    回覆時間：1–2 個工作天
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 px-5 py-3.5">
                <div className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-full shrink-0">
                  <i className="ri-building-line text-zinc-500 text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#18181B] mb-0.5">公司資訊</p>
                  <p className="text-xs text-zinc-500 leading-relaxed">
                    曜泰貿易有限公司<br />
                    統一編號：60347163
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/shop"
              className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-[#1B4332] text-white text-sm font-medium tracking-wider hover:bg-zinc-700 transition-colors duration-200 rounded-sm cursor-pointer whitespace-nowrap"
            >
              繼續購物
            </Link>
            <Link
              to="/"
              className="flex-1 inline-flex items-center justify-center px-6 py-3 border border-zinc-200 text-zinc-600 text-sm font-medium tracking-wider hover:border-zinc-400 transition-colors duration-200 rounded-sm cursor-pointer whitespace-nowrap"
            >
              回到首頁
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CheckoutPage() {
  const { items, clearCart } = useCart();
  const [showModal, setShowModal] = useState(false);
  const [orderData, setOrderData] = useState<OrderData | null>(null);

  // 只有在沒有訂單資料且未顯示 Modal 時才跳轉
  if (items.length === 0 && !orderData && !showModal) {
    return <Navigate to="/shop" replace />;
  }

  const handleSuccess = (data: {
    name: string;
    phone: string;
    city: string;
    district: string;
    address: string;
    totalPrice: number;
  }) => {
    // 先設定訂單資料並顯示 Modal，最後再清空購物車
    // 確保顯示狀態在購物車清空之前已就位
    const newOrder: OrderData = {
      orderNumber: generateOrderNumber(),
      ...data,
    };
    setOrderData(newOrder);
    setShowModal(true);
    clearCart();
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <CartDrawer />

      {/* Success Modal */}
      {showModal && orderData && (
        <SuccessModal
          order={orderData}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Page header */}
      <div className="pt-16 bg-[#1B4332]">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <h1 className="text-4xl font-extralight text-white tracking-tight">結帳</h1>
          <p className="text-white/40 text-sm mt-2">填寫收件資訊以完成訂購</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12">
          {/* Form */}
          <div>
            <CheckoutForm
              items={items}
              totalPrice={items.reduce((sum, i) => sum + i.product.price * i.quantity, 0)}
              onSuccess={handleSuccess}
            />
          </div>

          {/* Summary */}
          <div>
            <OrderSummary />
          </div>
        </div>
      </div>
    </div>
  );
}